import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import { AppContext } from 'context/AppContext';
import { Campaign } from 'models/CommonType';
import { useCampaign, useCampaignStats } from 'services/CampaignService';
import { useTasksAuthenticated } from 'services/TaskService';
import { useTasks } from "utils/apis";
import { truncateString, urlify } from "utils/helpers";

import Download from 'components/Download';
import ChatModal from 'components/ChatModal';
import ChatButton from 'components/ChatModal/ChatButton';
import ChatIframe from 'components/ChatModal/ChatIframe';

import CampaignJoinBanner from './components/CampaignJoinBanner';
import OrganizerBox from './components/OrganizerBox';
import CampaignTabs from './components/CampaignTabs';
import PrivateCampaignMessage from './components/PrivateCampaignMessage';
import CampaignStatsSection from './components/CampaignStatsSection';
import CampaignPageSkeleton from './components/CampaignPageSkeleton';
import SkillTags from './components/SkillTags';

import './CampaignPage.css';

const CampaignInfo = styled.div<{ hasChat?: boolean }>`
  display: flex;
  flex-direction: row;
  background-color: var(--color-bg-secondary);
  padding: var(--spacing-3xl);

  @media only screen and (max-width: 576px) {
    padding: 0;
    flex-direction: column;
  }
`;

const CampaignTitle = styled.div<{ hasChat?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start; /* Remove space-around to fix excessive spacing */
  gap: 16px; /* Standard spacing between elements */
  padding-left: var(--spacing-3xl);
  padding-right: var(--spacing-3xl);

  @media only screen and (max-width: 576px) {
    padding: var(--spacing-3xl) var(--spacing-xl);
    gap: 12px; /* Smaller gap on mobile */
  }
`;

const CampaignImg = styled.img<{ hasChat?: boolean }>`
  height: ${props => props.hasChat ? '350px' : '500px'};
  width: 100%;
  max-width: ${props => props.hasChat ? '100%' : '60%'};
  object-fit: cover;
  border-radius: var(--radius-lg);

  @media only screen and (max-width: 576px) {
    max-width: 100%;
    aspect-ratio: 4 / 3;
    height: auto;
    border-radius: 0;
    object-fit: contain;
    background-color: #000;
  }
`;

const CampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { authentication } = useContext(AppContext);
  const { authKey } = authentication;
  const [searchParams] = useSearchParams();
  const [imageError, setImageError] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [taskSortBy, setTaskSortBy] = useState('task_order');

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const campaignId = searchParams.get("id") || ''
  const { data: campaignData, isError: campaignError, isLoading: isCampaignLoading, mutate: campaignMutate } = useCampaign(campaignId, authKey)
  const { data: campaignStats, isLoading: isStatsLoading } = useCampaignStats(campaignId, authKey);

  // Determine which hook to use based on campaign privacy and join status
  const campaign = campaignData as Campaign
  const isPrivateAndJoined = campaign?.is_private && campaign?.is_joined
  const shouldUseAuthenticatedCall = isPrivateAndJoined

  // Use unauthenticated API (apis.jsx) for public campaigns
  const { data: publicTasks, mutate: mutatePublicTasks } = useTasks(campaignId, taskSortBy)

  // Use authenticated TaskService for private joined campaigns (requires token)
  const { data: privateTasks, mutate: mutatePrivateTasks } = useTasksAuthenticated(campaignId, authKey, shouldUseAuthenticatedCall, taskSortBy)

  // Select the appropriate tasks based on campaign type
  const tasks = shouldUseAuthenticatedCall ? privateTasks : publicTasks

  useEffect(() => {
    if (!isEmpty(campaignError)) {
      navigate('/');
    }
  }, [campaignError, navigate]);

  // Show skeleton loading state if data is loading
  if (isCampaignLoading || isEmpty(campaign) || !campaign.id) {
    return <CampaignPageSkeleton />;
  }

  // Determine if banner should be shown
  // Only show banner when user is logged in, campaign is not joined, and user hasn't joined yet
  const shouldShowBanner = !isEmpty(campaign) &&
    !campaign.is_joined &&
    authKey !== '';

  // Determine what to show in tasks section
  const shouldShowTasks = !campaign.is_private || campaign.is_joined;

  // Debug logging - remove after fixing
  console.log('Campaign debug:', {
    is_private: campaign.is_private,
    is_joined: campaign.is_joined,
    shouldShowTasks,
    shouldUseAuthenticatedCall,
    authKey: !!authKey,
    tasks: tasks?.length || 0
  });

  // Fallback: if user is authenticated and we have tasks, show them regardless
  const forceShowTasks = shouldShowTasks || (authKey && tasks && tasks.length > 0);
  
  // Determine if all necessary data is loaded before showing tabs
  const isAllDataLoaded = !isCampaignLoading && !isStatsLoading && !!campaign && !!tasks;

  // Enhanced refetch function
  const handleCampaignRefetch = () => {
    campaignMutate();
    // Force a page reload to refresh tasks data since useTasks doesn't have mutate
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Handle task sorting change
  const handleTaskSortChange = (newSortBy: string) => {
    setTaskSortBy(newSortBy);
    // Trigger refetch of tasks with new sorting
    if (shouldUseAuthenticatedCall) {
      mutatePrivateTasks();
    } else {
      mutatePublicTasks();
    }
  };

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  // Handle chat modal
  const handleChatOpen = () => {
    setIsChatModalOpen(true);
  };

  const handleChatClose = () => {
    setIsChatModalOpen(false);
  };

  return (
    <>
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{campaign.name}</title>
        <meta name="description" content={campaign.description} />
        <meta name="author" content={`${campaign.user?.first_name} ${campaign.user?.last_name}`} />

        {/* <meta property="og:url" content="" /> */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={campaign.name} />
        <meta property="og:description" content={campaign.description} />
        <meta property="og:site_name" content="Goodmates" />
        <meta property="og:image" content={campaign.user?.profile?.image_small_url} />
      </Helmet>

      <CampaignInfo hasChat={!!campaign?.dify_url} className={campaign?.dify_url ? 'has-chat-layout' : ''}>
        {campaign?.dify_url ? (
          // Chat available: image+description in column 1, chat in column 2
          <>
            <div className="campaign-content">
              {campaign.image_url && !imageError && (
                <CampaignImg 
                  src={campaign.image_url} 
                  alt={campaign.name}
                  onError={handleImageError}
                  hasChat={!!campaign?.dify_url}
                />
              )}

              <CampaignTitle hasChat={!!campaign?.dify_url}>
                <div className="CampaignPageTitle">{campaign.name}</div>
                
                {/* Skills tags below campaign name */}
                {campaign.skills && campaign.skills.length > 0 && (
                  <SkillTags skills={campaign.skills} />
                )}
                
                {/* Description below skills - web only */}
                <div className="desktop-only">
                  {campaign.description && (
                    <div className="CampaignPageDescription" 
                         dangerouslySetInnerHTML={{ __html: urlify(campaign.description) }} />
                  )}
                </div>
                
                {/* Campaign overview section - mobile only */}
                <div className="mobile-only campaign-overview-mobile">
                  <CampaignStatsSection
                    campaign={campaign}
                    campaignStats={campaignStats}
                    isLoading={isStatsLoading}
                    hideTitleOnMobile={true}
                  />
                </div>
                
                {/* Show chat button only on mobile when chat URL is available */}
                <div className="mobile-only" style={{ margin: '8px 0' }}>
                  <ChatButton 
                    onClick={handleChatOpen}
                    chatUrl={campaign.dify_url}
                    variant="campaign"
                    showInitialAnimation={true}
                  />
                </div>
              </CampaignTitle>
            </div>
          </>
        ) : (
          // No chat: original layout - image in column 1, title+description in column 2
          <>
            {campaign.image_url && !imageError && (
              <CampaignImg 
                src={campaign.image_url} 
                alt={campaign.name}
                onError={handleImageError}
                hasChat={false}
              />
            )}

            <CampaignTitle hasChat={false}>
              <div className="CampaignPageTitle">{campaign.name}</div>
              
              {/* Skills tags below campaign name */}
              {campaign.skills && campaign.skills.length > 0 && (
                <SkillTags skills={campaign.skills} />
              )}
              
              {/* Description below skills - web only */}
              <div className="desktop-only">
                {campaign.description && (
                  <div className="CampaignPageDescription" 
                       dangerouslySetInnerHTML={{ __html: urlify(campaign.description) }} />
                )}
              </div>
              
              {/* Campaign overview section - mobile only */}
              <div className="mobile-only">
                <CampaignStatsSection
                  campaign={campaign}
                  campaignStats={campaignStats}
                  isLoading={isStatsLoading}
                  hideTitleOnMobile={true}
                />
              </div>
            </CampaignTitle>
          </>
        )}
        
        {/* Chat iframe for desktop only */}
        {campaign?.dify_url && (
          <div className="desktop-only campaign-chat-iframe-container">
            <ChatIframe 
              chatUrl={campaign.dify_url}
              title={campaign.name}
              className="campaign-chat-iframe"
            />
          </div>
        )}
      </CampaignInfo>

      {/* Desktop stats section */}
      <div className="CampaignPage">
        <div className="desktop-only">
          <CampaignStatsSection
            campaign={campaign}
            campaignStats={campaignStats}
            isLoading={isStatsLoading}
          />
        </div>

        {/* Show organizer/organizer team on desktop only - mobile shows in Details tab */}
        <div className="desktop-only">
          {campaign.users && campaign.users.length > 0 ? (
            <>
              <div className="CampaignPageLabel">
                {t('#Campaign.organizer_team')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {(() => {
                  // Create a unique list ensuring campaign.user is included and no duplicates
                  const allOrganizers = [];
                  const seenIds = new Set();
                  
                  // Always include the main organizer first
                  if (campaign.user && campaign.user.id && !seenIds.has(campaign.user.id)) {
                    allOrganizers.push(campaign.user);
                    seenIds.add(campaign.user.id);
                  }
                  
                  // Add users from the team, avoiding duplicates
                  campaign.users.forEach(user => {
                    if (user && user.id && !seenIds.has(user.id)) {
                      allOrganizers.push(user);
                      seenIds.add(user.id);
                    }
                  });
                  
                  return allOrganizers.map((user, index) => (
                    <OrganizerBox
                      key={user.id || index}
                      avatar={user.profile?.image_small_url}
                      user={user}
                      bio={truncateString(user.profile?.bio ?? '', 200)}
                    />
                  ));
                })()
                }
              </div>
            </>
          ) : (
            <>
              <div className="CampaignPageLabel">{t('#Campaign.organizer')}</div>
              <OrganizerBox
                avatar={campaign.user?.profile?.image_small_url}
                user={campaign.user}
                bio={truncateString(campaign.user?.profile?.bio ?? '', 200)}
              />
            </>
          )}
        </div>

        {/* Replace tasks section with tabs - only show when all data is loaded */}
        {forceShowTasks && isAllDataLoaded && (
          <>
            <CampaignTabs
              campaign={campaign}
              tasks={tasks}
              authKey={authKey}
              isMobile={isMobile}
              taskSortBy={taskSortBy}
              onTaskSortChange={handleTaskSortChange}
            />
          </>
        )}

        {/* Show tasks section or loading state */}
        {!forceShowTasks && isAllDataLoaded && (
          <>
            <div className="CampaignPageLabel">{t('#Campaign.tasks')}</div>
            {authKey ? (
              <PrivateCampaignMessage />
            ) : (
              <Download />
            )}
          </>
        )}
        
        {/* Show loading placeholder for tasks section when data is not ready */}
        {!isAllDataLoaded && (
          <>
            <div className="CampaignPageLabel">{t('#Campaign.tasks')}</div>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div>Loading tasks...</div>
            </div>
          </>
        )}
      </div>

      {shouldShowBanner && (
        <CampaignJoinBanner campaign={campaign} refetchCampaign={handleCampaignRefetch} />
      )}

    </div>
    
    {/* Chat modal */}
    {campaign?.dify_url && (
      <ChatModal
        isOpen={isChatModalOpen}
        closeHandler={handleChatClose}
        chatUrl={campaign.dify_url}
        title={campaign.name}
      />
    )}
    </>
  );
}

export default CampaignPage;